/* eslint-disable react/no-unstable-nested-components,react/forbid-prop-types */
import PropTypes from 'prop-types';
import React from 'react';
import PrevewIcon from '@mui/icons-material/Visibility';
import FastForwardIcon from '@mui/icons-material/FastForward';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import BoltIcon from '@mui/icons-material/Bolt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import withTheme from '@mui/styles/withTheme';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import { byIdMap } from 'relevant-shared/objects/objectStore';
import { Link } from '../Link/Link';
import BrowserUtils from '../../lib/browserUtils';
import validation from '../../lib/validation';
import { triggerToast } from '../Toast/misc';
import GlobalEventDiv from '../../lib/globalEventDiv';
import SystemData from '../../lib/systemData';
import { SimpleAlert, FormOf, Scope } from '../Wrappers';
import PopupSelector from '../PopupSelector';
import Base from '../../layouts/Base';
import { Toast } from '../Toast/Toast';
import { ActionButton } from '../ActionButton/ActionButton';
import PreviewSettings from './previewSettings';
import progressMsgChannel from '../../lib/progressMsgChannel';
import { Dialog } from '../Dialog';
import classes from '../../api/classes';
import { ensureLoaded } from '../../lib/objectStoreUtils';

const { TagPreview } = classes;

const zCss = (toAdd = 0) => `calc(var(--z-live-preview) + ${toAdd})`;

const muiPopupThemeOverrides = {
	components: {
		MuiPopover: { // Ensures <Select> menu items are not hidden below UI
			styleOverrides: {
				root: {
					zIndex: zCss(1),
				},
			},
		},
		MuiTooltip: { // ...and the same for <Tooltip>
			styleOverrides: {
				popper: {
					zIndex: zCss(1),
				},
			},
		},
		MuiFormHelperText: { // Don't use any layout space for form error texts, to avoid "jumping"
			styleOverrides: {
				root: {
					'&.Mui-error': {
						minHeight: 0,
						marginTop: 0,
						position: 'absolute',
						left: 0,
						top: '100%',
						background: 'white',
						transform: 'translateY(4px)',
						zIndex: 2,
						paddingLeft: 3,
						paddingRight: 3,
					},
				},
			},
		},
	},
};

class TagPreviewEditor extends React.Component {
	constructor(props) {
		super(props);
		const { publisher } = props;
		this.byPubCache = {}; // Re-use previews when switching publisher on global pages
		this.state = { preview: new TagPreview({ publisherId: publisher?.id || null }) };
		if (window.location.toString().includes('openLivePreview')) {
			setTimeout(() => this.openPreview());
		}
	}

	getWidthCssProp() {
		const { theme } = this.props;
		// eslint-disable-next-line react/prop-types
		const mdWidth = theme.breakpoints.values.md;
		return `max(300px, (100vw - ${mdWidth}px) / 2)`;
	}

	getPublisher() {
		const { publisher, pubSelect } = this.props;
		const { preview } = this.state;
		return pubSelect ? byIdMap('Publisher')[preview.publisherId] : publisher;
	}

	getPreview() {
		const { preview } = this.state;
		return preview;
	}

	openPreview() {
		Base.renderGlobal((close) => Object.assign(this, { closePopup: close }) && (
			<Scope
				content={(scope) => this.renderPopup({
					scope,
					close: () => {
						close();
						this.closePopup = null;
						this.setState({ open: false });
					},
				})}
			/>
		));
		this.setState({ open: true });
	}

	async generatePreview() {
		const {
			getOnScreenPubObject,
			getOnScreenTagObject,
			getOnScreenGlobalSettingsObject,
			dataId,
			form,
		} = this.props;
		const { preview: previewObj } = this.state;
		let pubObject;
		let tagObject;
		let globalObject;
		if (form?.hasErrors(false, true)) {
			Base.renderGlobal((done) => (
				<Dialog
					open
					status="error"
					text="Please, correct the errors first"
					onClose={done}
				/>
			));
			return null;
		}
		if (previewObj.type === 'screen') {
			pubObject = getOnScreenPubObject?.();
			tagObject = getOnScreenTagObject?.();
			globalObject = getOnScreenGlobalSettingsObject?.();
		}
		const newPreview = await TagPreview.generate({
			previewObj,
			pubObject,
			tagObject,
			globalObject,
			dataId,
			msgChannel: progressMsgChannel({ maxProgressMsgs: 30 }),
		});
		return newPreview;
	}

	renderContent(params) {
		const { pubSelect } = this.props;
		const { op, form, scope, model: preview } = params;
		const setPreview = (newPreview) => {
			if (!newPreview.isNew) {
				this.byPubCache[newPreview.publisherId] = newPreview;
			}
			this.setState({ preview: newPreview }, () => scope.update());
		};
		const fragmentStr = `#rlvLPreview=${preview.id}`;
		let { exampleUrl: url } = preview;
		if (url && validation.url().valid(url) && !url.includes('#')) {
			url += fragmentStr;
			if (!url.startsWith('https://') && !url.startsWith('http://')) {
				url = `https://${url}`;
			}
		} else {
			url = null;
		}
		const previewSettings = (
			<PreviewSettings
				{...params}
				pubSelect={pubSelect}
				setPreview={setPreview}
				byPubCache={this.byPubCache}
			/>
		);
		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					{previewSettings}
				</Grid>
				<Grid item xs={12}>
					<ActionButton
						icon={<BoltIcon style={{ color: 'yellow' }} />}
						label={`${preview.isNew ? 'Generate' : 'Update'} live preview link`}
						disabled={form.hasErrors(true) || !this.getPublisher()}
						onClick={() => op.reload(async () => {
							const newPreview = await this.generatePreview();
							if (newPreview) {
								setPreview(newPreview);
							}
						})}
					/>
				</Grid>
				{!preview.isNew && (
					<Grid item xs={12} style={{ paddingTop: 0 }}>
						<span style={{ fontWeight: 300 }}>URL fragment to append</span>
						<Tooltip title="Copy to clipboard">
							<IconButton
								aria-label="Copy to clipboard"
								onClick={() => {
									BrowserUtils.copyTextToClipboard(fragmentStr);
									triggerToast({
										message: 'Copied to clipboard',
										status: 'success',
										timeout: 2,
									});
								}}
								size="large"
							>
								<ContentCopyIcon />
							</IconButton>
						</Tooltip>
						<pre style={{ fontSize: 'small', background: 'aliceblue' }}>
							{fragmentStr}
						</pre>
					</Grid>
				)}
				{!preview.isNew && url && (
					<Grid item xs={12}>
						<Link target="_blank" href={url} rel="noreferrer">
							Full example destination URL
						</Link>
					</Grid>
				)}
			</Grid>
		);
	}

	renderPopup({ close, scope }) {
		const { theme } = this.props;
		const publisher = this.getPublisher();
		const width = this.getWidthCssProp();
		const popupTheme = createTheme(theme, muiPopupThemeOverrides);
		const dialogProps = {
			sx: { zIndex: zCss(), width },
			PaperProps: {
				sx: {
					position: 'fixed',
					left: 0,
					margin: 0,
					maxHeight: '100vh',
					height: '100vh',
					width,
					borderBottomLeftRadius: 0,
					borderTopLeftRadius: 0,
				},
			},
		};
		return (
			<PopupSelector
				canCancel={false}
				onApplyChanges={close}
				noTakeover
				forceExpanded
				hideLink
				fn={ensureLoaded}
				dialogTitleProps={{ sx: { padding: 0 } }}
				title={(
					<SimpleAlert
						title="Live preview"
						alertProps={{ sx: { mt: 0 } }}
						content={(
							<>
								Live preview will work on all sites loading any of the tag .js files for any of the
								sites in {publisher ? <b>{publisher.name}</b> : 'the selected publisher account'}.
							</>
						)}
					/>
				)}
				dialogProps={dialogProps}
				okLabel="Close"
				actionButtonProps={{
					icon: <FastRewindIcon />,
					variant: 'outlined',
				}}
				content={(popup) => (
					<ThemeProvider theme={popupTheme}>
						<FormOf
							model={this.getPreview()}
							content={(p) => this.renderContent({ ...p, popup, op: popup.op, zCss, scope })}
						/>
					</ThemeProvider>
				)}
			/>
		);
	}

	renderToast() {
		return (
			<Toast
				open
				status="info"
				position="bottom-left"
				style={{ alignItems: 'center' }}
				icon={<PrevewIcon />}
				closeable={false}
				message={() => (
					<ActionButton
						icon={<FastForwardIcon />}
						label="Live preview"
						onClick={() => this.openPreview()}
						variant="outlined"
					/>
				)}
			/>
		);
	}

	render() {
		const { children, publisher, extraPushPx, pubSelect } = this.props;
		const { open } = this.state;
		const canUse = SystemData.genericData.hbmEnabled
			&& (publisher || pubSelect)
			&& !publisher?.isNew;
		return (
			<GlobalEventDiv
				triggerOnUnmount
				style={{ transition: 'padding-left 0.3s' }}
				callback={(div, { type }) => {
					if (type === 'unmount') {
						// Quick-fix for issue with popup staying when navigating back
						this.closePopup?.();
					} else {
						div.style.paddingLeft = !open ? '0px' : `calc(${this.getWidthCssProp()} - ${div.getBoundingClientRect().left - extraPushPx}px)`;
					}
				}}
			>
				<Grid container spacing={3}>
					{!open && !!canUse && this.renderToast()}
					{children}
				</Grid>
			</GlobalEventDiv>
		);
	}
}

TagPreviewEditor.propTypes = {
	publisher: PropTypes.object,
	getOnScreenPubObject: PropTypes.func,
	getOnScreenTagObject: PropTypes.func,
	getOnScreenGlobalSettingsObject: PropTypes.func,
	dataId: PropTypes.object,
	form: PropTypes.object,
	children: PropTypes.node,
	theme: PropTypes.object.isRequired,
	extraPushPx: PropTypes.number,
	pubSelect: PropTypes.bool,
};

TagPreviewEditor.defaultProps = {
	publisher: undefined,
	getOnScreenPubObject: undefined,
	getOnScreenTagObject: undefined,
	getOnScreenGlobalSettingsObject: undefined,
	dataId: undefined,
	form: undefined,
	children: undefined,
	extraPushPx: 0,
	pubSelect: false,
};

export default withTheme(TagPreviewEditor);
